import { useEffect, useState } from "react";
import API from "../lib/api";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Buildings } from "@phosphor-icons/react";

/**
 * Admin forms (request / job / contract): pick a property from the client's portfolio to fill the address.
 * Typed addresses are saved to the client's portfolio automatically on submit.
 *
 * props: clientId, clientLabel, propertyId, onPick(property|null), testid
 */
export default function ClientPropertyPicker({ clientId, clientLabel, propertyId, onPick, testid = "client-property" }) {
  const [props, setProps] = useState([]);
  useEffect(() => {
    if (!clientId) { setProps([]); return; }
    let cancelled = false;
    API.get(`/clients/${clientId}/properties`).then(({ data }) => { if (!cancelled) setProps((data.properties || []).filter(p => p.active !== false)); }).catch(() => {});
    return () => { cancelled = true; };
  }, [clientId]);

  if (!clientId) return null;
  return (
    <div className="border border-border rounded-md p-3 bg-muted/20 space-y-2" data-testid={`${testid}-picker`}>
      <div className="flex items-center gap-2">
        <Buildings size={14} className="text-primary" />
        <Label className="text-xs">{clientLabel ? `${clientLabel}'s properties` : "Client's properties"} <span className="text-muted-foreground font-normal">({props.length})</span></Label>
      </div>
      {props.length > 0 && (
        <Select value={propertyId || "__none"} onValueChange={(v) => onPick(v === "__none" ? null : props.find(p => p.id === v) || null)}>
          <SelectTrigger className="h-8 text-xs" data-testid={`${testid}-select`}><SelectValue placeholder="Choose a registered property to fill the address" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">— Type a new address —</SelectItem>
            {props.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {[p.address_line, p.postcode].filter(Boolean).join(", ")}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {!propertyId && (
        <p className="text-[11px] text-muted-foreground" data-testid={`${testid}-auto-note`}>
          New address? It's saved to {clientLabel ? `${clientLabel}'s` : "the client's"} portfolio automatically — with site contact and access details — so it can be picked next time.
        </p>
      )}
      {propertyId && <p className="text-[11px] text-muted-foreground">Address filled from the portfolio — edit the fields below if anything has changed.</p>}
    </div>
  );
}
